import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Network, BarChart2, Search, Filter, Link2 } from 'lucide-react';
import GraphVisualization from '../../components/graph/GraphVisualization';
import {
  getDocumentGraph,
  getEntityNetwork,
  getGraphStats,
  getDocumentCitations,
} from '../../api/knowledge';
import type { GraphNode, NodeType, GraphStats } from '../../types/knowledge';
import { NODE_COLORS } from '../../types/knowledge';

const ALL_NODE_TYPES: NodeType[] = ['Document', 'Regulation', 'Organization', 'Entity', 'Tag'];

export default function KnowledgeGraphPage() {
  const [searchType, setSearchType] = useState<'document' | 'entity'>('document');
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState<{ type: string; value: string } | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [filterTypes, setFilterTypes] = useState<NodeType[]>([]);
  const [depth, setDepth] = useState(2);

  const { data: graphData, isLoading } = useQuery({
    queryKey: ['graph', activeQuery],
    queryFn: () => {
      if (!activeQuery) return null;
      if (activeQuery.type === 'document') return getDocumentGraph(activeQuery.value, depth);
      return getEntityNetwork(activeQuery.value);
    },
    enabled: !!activeQuery,
  });

  const { data: stats } = useQuery<GraphStats>({
    queryKey: ['graph-stats'],
    queryFn: getGraphStats,
  });

  const handleSearch = () => {
    if (!searchInput.trim()) return;
    setActiveQuery({ type: searchType, value: searchInput.trim() });
    setSelectedNode(null);
  };

  const toggleFilter = (type: NodeType) => {
    setFilterTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-800">Knowledge Graph</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Visualisasi hubungan antar dokumen, regulasi, organisasi, dan entitas
        </p>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Left Panel */}
        <div className="w-72 flex-shrink-0 space-y-4 overflow-y-auto">
          {/* Search */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Cari Node</p>
            <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-0.5">
              {(['document', 'entity'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setSearchType(t)}
                  className={`flex-1 text-xs py-1 rounded-md font-medium transition ${
                    searchType === t ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {t === 'document' ? 'Dokumen ID' : 'Entitas'}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder={searchType === 'document' ? 'ID Dokumen...' : 'Nama entitas...'}
                className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button onClick={handleSearch} className="bg-blue-600 text-white px-2.5 rounded-lg hover:bg-blue-700">
                <Search size={14} />
              </button>
            </div>
          </div>

          {/* Depth control */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Kedalaman Graf</p>
            <input
              type="range" min={1} max={3} value={depth}
              onChange={e => setDepth(parseInt(e.target.value))}
              className="w-full accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1 (langsung)</span><span>{depth}</span><span>3 (luas)</span>
            </div>
          </div>

          {/* Filter by type */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1">
              <Filter size={12} /> Filter Tipe Node
            </p>
            <div className="space-y-1.5">
              {ALL_NODE_TYPES.map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!filterTypes.length || filterTypes.includes(type)}
                    onChange={() => toggleFilter(type)}
                    className="rounded accent-blue-600"
                  />
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: NODE_COLORS[type] }} />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-1">
                <BarChart2 size={12} /> Statistik Graf
              </p>
              <div className="space-y-1">
                {Object.entries(stats.nodes || {}).map(([type, count]) => (
                  <div key={type} className="flex justify-between text-xs">
                    <span className="text-gray-600">{type}</span>
                    <span className="font-semibold text-gray-800">{count}</span>
                  </div>
                ))}
                <div className="border-t border-gray-100 mt-2 pt-2">
                  {Object.entries(stats.relationships || {}).map(([rel, count]) => (
                    <div key={rel} className="flex justify-between text-xs">
                      <span className="text-gray-400">{rel}</span>
                      <span className="text-gray-600">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Selected node detail */}
          {selectedNode && (
            <div className="bg-white rounded-xl border border-blue-200 p-4">
              <p className="text-xs font-semibold text-blue-600 uppercase mb-2">Node Dipilih</p>
              <div
                className="inline-block px-2 py-0.5 rounded-full text-xs text-white mb-2"
                style={{ backgroundColor: NODE_COLORS[selectedNode.type as NodeType] || '#64748B' }}
              >
                {selectedNode.type}
              </div>
              <p className="text-sm font-medium text-gray-800 mb-2">{selectedNode.label}</p>
              <div className="space-y-1">
                {Object.entries(selectedNode.properties).slice(0, 4).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <span className="text-gray-400">{k}: </span>
                    <span className="text-gray-700">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Citations panel — only when a Document node is selected */}
          {selectedNode?.type === 'Document' && (
            <CitationsPanel docId={selectedNode.id} />
          )}
        </div>

        {/* Graph area below */}
        <div className="flex-1 min-w-0">
          {!activeQuery && (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <div className="text-center text-gray-400">
                <Network size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Cari dokumen atau entitas untuk memvisualisasikan graf</p>
                <p className="text-xs mt-1">Gunakan panel kiri untuk memulai</p>
              </div>
            </div>
          )}
          {isLoading && (
            <div className="h-full flex items-center justify-center bg-gray-50 rounded-xl">
              <div className="text-center text-gray-400">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm">Memuat graf...</p>
              </div>
            </div>
          )}
          {graphData && !isLoading && (
            <GraphVisualization
              data={graphData}
              onNodeClick={setSelectedNode}
              height={window.innerHeight - 240}
              filterTypes={filterTypes}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function CitationsPanel({ docId }: { docId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['document-citations', docId],
    queryFn: () => getDocumentCitations(docId),
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs text-gray-400">Memuat sitasi...</p>
      </div>
    );
  }
  const outgoing = data?.outgoing ?? [];
  const incoming = data?.incoming ?? [];
  if (outgoing.length === 0 && incoming.length === 0) {
    return null;
  }

  const confidenceBadge = (c: number) => {
    if (c >= 0.9) return 'bg-emerald-100 text-emerald-700';
    if (c >= 0.7) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className="bg-white rounded-xl border border-red-200 p-4">
      <p className="text-xs font-semibold text-red-600 uppercase mb-2 flex items-center gap-1">
        <Link2 size={12} /> Sitasi
      </p>

      {outgoing.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1.5">
            Merujuk ke <span className="font-semibold">{outgoing.length}</span> dokumen:
          </p>
          <div className="space-y-1.5">
            {outgoing.map((c, idx) => (
              <div key={`out-${idx}`} className="text-xs border-l-2 border-red-300 pl-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-800 font-medium truncate">{c.judul}</span>
                  <span className={`px-1.5 py-0.5 rounded ${confidenceBadge(c.confidence)} flex-shrink-0`}>
                    {(c.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                {c.nomor_dokumen && (
                  <p className="text-gray-500 mt-0.5">{c.nomor_dokumen}</p>
                )}
                {c.source_pasal && (
                  <p className="text-gray-400 italic mt-0.5">via {c.source_pasal}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {incoming.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1.5">
            Dirujuk oleh <span className="font-semibold">{incoming.length}</span> dokumen:
          </p>
          <div className="space-y-1.5">
            {incoming.map((c, idx) => (
              <div key={`in-${idx}`} className="text-xs border-l-2 border-blue-300 pl-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-800 font-medium truncate">{c.judul}</span>
                  <span className={`px-1.5 py-0.5 rounded ${confidenceBadge(c.confidence)} flex-shrink-0`}>
                    {(c.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                {c.nomor_dokumen && (
                  <p className="text-gray-500 mt-0.5">{c.nomor_dokumen}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
